<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/fields">Fields</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Field' : 'Add Field' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Field' : 'Add Field' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column - Main Form -->
    <div class="col-lg-8">
      <!-- Customer Selection -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-person me-2"></i>Customer</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Assign to Customer</label>
              <select class="form-select" formControlName="customerId">
                <option value="">No customer (unassigned)</option>
                <option *ngFor="let customer of customerOptions()" [value]="customer.id">{{ customer.name }}</option>
              </select>
              <div class="form-text d-flex justify-content-between align-items-center">
                <span>Fields can be assigned to a customer or left unassigned</span>
                <button type="button" class="btn btn-link btn-sm text-primary text-decoration-none p-0" (click)="openCustomerQuickAdd()">
                  <i class="bi bi-plus-circle me-1"></i>New Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Basic Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-8">
              <label class="form-label">Field Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name"
                     placeholder="e.g., North Field - Section A"
                     [class.is-invalid]="isFieldInvalid('name')">
              <div class="invalid-feedback">Please enter a field name (at least 2 characters)</div>
            </div>
            <div class="col-md-4">
              <label class="form-label">Acreage <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="acreage"
                       placeholder="45.5" step="0.1" min="0.1"
                       [class.is-invalid]="isFieldInvalid('acreage')">
                <span class="input-group-text">ac</span>
              </div>
              <div class="invalid-feedback">Please enter a valid acreage</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Crop Type <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="cropType"
                      [class.is-invalid]="isFieldInvalid('cropType')">
                <option value="">Select crop type...</option>
                <option *ngFor="let crop of cropTypes" [value]="crop">{{ crop }}</option>
              </select>
              <div class="invalid-feedback">Please select a crop type</div>
            </div>
            @if (isOtherCropType) {
              <div class="col-md-6">
                <label class="form-label">Specify Crop Type <span class="text-danger">*</span></label>
                <input type="text" class="form-control" formControlName="customCropType"
                       placeholder="e.g., Sunflowers, Hemp, Pecans"
                       maxlength="100"
                       [class.is-invalid]="isFieldInvalid('customCropType')">
                <div class="invalid-feedback">Please specify the crop type</div>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Location Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Location</h5>
        </div>
        <div class="card-body">
          @if (!isEditMode || isFieldLoaded) {
            <app-location-panel
              [latitude]="initialLatitude"
              [longitude]="initialLongitude"
              [address]="initialAddress"
              (coordinatesChanged)="onCoordinatesChanged($event)"
            ></app-location-panel>
          }
        </div>
      </div>

      <!-- Field Boundary -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0">
            <a class="text-decoration-none d-flex align-items-center"
               role="button"
               (click)="isBoundaryExpanded = !isBoundaryExpanded"
               [attr.aria-expanded]="isBoundaryExpanded">
              <i class="bi bi-bounding-box me-2"></i>Field Boundary
              @if (boundaryGeojson) {
                <span class="badge bg-success ms-2 small fw-normal">Defined</span>
              }
              @if (isAgremoSynced) {
                <span class="badge bg-success ms-1 small fw-normal"><i class="bi bi-leaf me-1"></i>Synced</span>
              }
              <i class="bi ms-auto small" [class.bi-chevron-down]="!isBoundaryExpanded" [class.bi-chevron-up]="isBoundaryExpanded"></i>
            </a>
          </h5>
        </div>
        @if (isBoundaryExpanded) {
          <div class="card-body">
            @if (hasCoordinates) {
              <app-boundary-editor
                [boundaryGeojson]="boundaryGeojson"
                [latitude]="effectiveLatitude"
                [longitude]="effectiveLongitude"
                [fieldId]="fieldId"
                (boundaryChanged)="onBoundaryChanged($event)"
                (acreageChanged)="onAcreageChanged($event)"
                (originalFileChanged)="onOriginalFileChanged($event)"
              ></app-boundary-editor>

              <!-- Sync to Agremo button (visible only for existing fields) -->
              @if (isEditMode && fieldId) {
                <div class="mt-3 d-flex align-items-center gap-2">
                  <button type="button"
                          class="btn btn-sm"
                          [class.btn-outline-primary]="!isAgremoSynced"
                          [class.btn-outline-success]="isAgremoSynced"
                          [disabled]="!boundaryGeojson || isSyncingAgremo || isAgremoSynced || isAgremoConfigured() !== true || form.dirty"
                          (click)="syncToAgremo()"
                          [title]="isAgremoSynced ? 'Already synced to Agremo' : form.dirty ? 'Save field changes before syncing' : 'Sync this field to Agremo for crop analysis'">
                    @if (isSyncingAgremo) {
                      <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                      Syncing...
                    } @else if (isAgremoSynced) {
                      <i class="bi bi-leaf me-1"></i>Synced to Agremo
                    } @else if (agremoFieldId) {
                      <i class="bi bi-arrow-repeat me-1"></i>Re-sync to Agremo
                    } @else {
                      <i class="bi bi-cloud-upload me-1"></i>Sync to Agremo
                    }
                  </button>
                  @if (agremoSyncSuccess) {
                    <span class="small text-success">
                      <i class="bi bi-check-circle me-1"></i>Synced successfully
                    </span>
                  }
                  @if (agremoSyncError) {
                    <span class="small text-danger">
                      <i class="bi bi-x-circle me-1"></i>{{ agremoSyncError }}
                    </span>
                  }
                </div>
              }
            } @else {
              <div class="text-muted text-center py-4">
                <i class="bi bi-geo-alt fs-3 d-block mb-2"></i>
                <p class="mb-0">Set a location above before adding a boundary</p>
              </div>
            }
          </div>
        }
      </div>

      <!-- Agremo Maps (visible in edit mode when field is synced to Agremo) -->
      @if (isEditMode && fieldId && agremoFieldId) {
        <app-field-maps-card [fieldId]="fieldId" [agremoFieldId]="agremoFieldId"></app-field-maps-card>
      }

      <!-- Notes -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-chat-left-text me-2"></i>Notes</h5>
        </div>
        <div class="card-body">
          <textarea appCharCounter class="form-control" formControlName="notes" rows="3"
                    placeholder="Any additional notes about this field..."></textarea>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="d-flex align-items-center gap-2 mb-3">
            <i class="bi bi-lightbulb text-warning"></i>
            Tips
          </h6>
          <ul class="small text-muted mb-0 ps-3">
            <li class="mb-2">GPS coordinates enable weather lookup for spray records</li>
            <li class="mb-2">Use descriptive names to easily identify fields</li>
            <li class="mb-2">Click "Get Current Location" to use your device's GPS</li>
            <li class="mb-2">Search for an address or click on the map to set coordinates</li>
            <li class="mb-2">Add a field boundary for accurate acreage and Agremo analysis</li>
            <li>Add notes for special considerations like drainage issues or soil types</li>
          </ul>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-item" *ngIf="form.get('name')?.value">
            <div class="summary-label text-muted">Name</div>
            <div class="summary-value">{{ form.get('name')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('acreage')?.value">
            <div class="summary-label text-muted">Acreage</div>
            <div class="summary-value">{{ form.get('acreage')?.value }} acres</div>
          </div>
          <div class="summary-item" *ngIf="form.get('cropType')?.value">
            <div class="summary-label text-muted">Crop Type</div>
            <div class="summary-value">{{ isOtherCropType && form.get('customCropType')?.value ? form.get('customCropType')?.value : form.get('cropType')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('latitude')?.value && form.get('longitude')?.value">
            <div class="summary-label text-muted">Coordinates</div>
            <div class="summary-value small">{{ form.get('latitude')?.value }}, {{ form.get('longitude')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('address')?.value">
            <div class="summary-label text-muted">Address</div>
            <div class="summary-value small">{{ form.get('address')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="boundaryGeojson">
            <div class="summary-label text-muted">Boundary</div>
            <div class="summary-value small text-success"><i class="bi bi-bounding-box me-1"></i>Defined</div>
          </div>
          <div class="summary-item" *ngIf="agremoFieldId">
            <div class="summary-label text-muted">Agremo</div>
            <div class="summary-value small" [class.text-success]="isAgremoSynced" [class.text-warning]="!isAgremoSynced">
              @if (isAgremoSynced) {
                <i class="bi bi-leaf me-1"></i>Synced
              } @else {
                <i class="bi bi-arrow-repeat me-1"></i>Needs re-sync
              }
            </div>
          </div>

          <hr class="my-3">

          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving">
              <ng-container *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Field' : 'Add Field' }}
              </ng-container>
              <ng-container *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </ng-container>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</form>

<!-- Customer Quick-Add Modal -->
<app-customer-quick-add #customerQuickAdd
  (customerCreated)="onCustomerCreated($event)"
></app-customer-quick-add>
